import type { Country, Language, Route } from '@application/types';
import { CountryCode } from '@gql/graphql';
import 'dayjs/locale/fr';
import 'dayjs/locale/nl';
import 'dayjs/locale/de';
import { findKey, intersection } from 'lodash/fp';
import { parse } from 'qs';
import { removeTrailingSlash } from './string';

export const PATH_PREFIX_REGEX =
  /((\/be\/nl\/)|(\/be\/fr\/)|(\/de\/de\/)|(\/ch\/fr\/)|(\/ch\/de\/)|(\/fr\/fr\/)|(\/eu\/en\/)|(\/nl\/nl\/))/;

export const countryFromPath = (path: string): Country => {
  const country = path.split('/')[1];
  return ['fr', 'nl', 'de', 'be', 'ch', 'eu'].includes(country) ? (country as Country) : 'be';
};

export const countryCodeFromCountry = (country: Country) => {
  const defaultCountryCode = CountryCode.BE;
  const countryCode = CountryCode[country.toUpperCase() as 'FR' | 'BE' | 'NL' | 'DE' | 'CH' | 'EU'];
  return countryCode ?? defaultCountryCode;
};
export const languageFromPath = (path: string): Language => {
  const code = path.split('/')[2];
  return ['fr', 'nl', 'de', 'en'].includes(code) ? (code as Language) : 'fr';
};

export const infoFromPath = (
  path: string,
  language: Language,
  country: Country,
  routes: any,
  location?: any,
) => {
  const pathWithoutPrefixes = path.replace(PATH_PREFIX_REGEX, '');
  const [pathWithoutQueryString, queryString] = removeTrailingSlash(pathWithoutPrefixes).split('?');
  const splitPath = pathWithoutQueryString.split('/');

  let foundRoute = Object.values(routes as string[]).find(
    (item) => item === pathWithoutQueryString,
  );
  if (!foundRoute) {
    const matches = Object.values(routes).map((routeValue) => {
      const splitRouteValue = (routeValue as string).split('/');
      const amount = intersection(splitRouteValue, splitPath).length;
      return { routeValue, matches: amount };
    });
    const noMatchesFound = matches.every((x) => x.matches === 0);
    foundRoute = noMatchesFound
      ? ''
      : (matches.sort((a, b) => b.matches - a.matches)[0].routeValue as string);
  }
  const route = findKey((r) => r === foundRoute, routes) as Route;
  const basePath = routes[route as any];

  const params = pathWithoutPrefixes
    .replace(basePath, '')
    .split('/')
    .filter(
      (param) =>
        param !== '' &&
        param !== country &&
        param !== language &&
        param !==
          basePath
            ?.replace('mijn-tipaw/', '')
            .replace('mon-tipaw/', '')
            .replace('mein-tipaw/', '')
            .replace('my-tipaw/', ''),
    );

  const search = parse(queryString ?? location?.search, { ignoreQueryPrefix: true });
  return { route, basePath, params, search, state: location?.state as any };
};
